import { useTranslation } from 'react-i18next';

import { KpiTile } from '@/modules/analytics/components/KpiTile/KpiTile';

import { formatMetricValue, formatShare } from './formatMetric';
import { ResolvedMetric } from './metricConfig';

interface Props {
  /** Ordered by `groupMetrics()`: debt amount, total promised, PPA, people. */
  tiles: ResolvedMetric[];
  promisedDebts: ResolvedMetric | null;
  /** `total_ptp_amount / debt_amount`, null when not computable. */
  promisedShare: number | null;
  currency?: string;
  isLoading?: boolean;
}

const SKELETON_TILES = 5;

/**
 * Share of the outstanding debt that has been promised, on the Total PTP tile. Split in
 * two because the percentage sits beside the amount (`value`) and the bar on its own row
 * (`comparison`).
 */
const PromisedShareValue = ({ amount, share }: { amount: string; share: number }) => (
  <span className="flex items-baseline gap-2">
    {amount}
    <span className="text-sm leading-5 font-medium text-success">{formatShare(share)}</span>
  </span>
);

/** Clamped: the promised amount can exceed the debt. */
const PromisedShareBar = ({ share }: { share: number }) => (
  <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-muted">
    <div
      className="h-full rounded-full bg-success"
      style={{ width: `${Math.min(Math.max(share, 0), 1) * 100}%` }}
    />
  </div>
);

const UnitCaption = ({ children }: { children: string }) => (
  <span className="mt-1 text-xs leading-4 text-muted-foreground">{children}</span>
);

export const CampaignResults = ({
  tiles,
  promisedDebts,
  promisedShare,
  currency,
  isLoading,
}: Props) => {
  const { t } = useTranslation();

  const allTiles = promisedDebts ? [...tiles, promisedDebts] : tiles;

  if (!isLoading && allTiles.length === 0 && promisedShare == null) {
    return null;
  }

  // A known currency renders its own symbol in the value, so the caption would repeat
  // it. Without one the value is a bare number and the caption is all the reader gets.
  const needsCurrencyCaption = (metric: ResolvedMetric) =>
    metric.format === 'currency' && !currency;

  return (
    <section>
      <h2 className="mb-4 text-base leading-6 font-semibold text-foreground">
        {t('campaigns.performance.sections.results')}
      </h2>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {isLoading
          ? Array.from({ length: SKELETON_TILES }, (_, index) => (
              <KpiTile isLoading key={index} label="" value={null} />
            ))
          : allTiles.map((metric) => {
              const amount = formatMetricValue(metric.value, metric.format, currency);
              const hasShare = metric.key === 'total_ptp_amount' && promisedShare != null;
              // The bar owns the comparison slot on the promised tile. That tile is
              // currency, so it only has a caption when the currency is unknown.
              const caption =
                !hasShare && needsCurrencyCaption(metric) ? (
                  <UnitCaption>{t('campaigns.performance.units.currency')}</UnitCaption>
                ) : undefined;

              return (
                <KpiTile
                  comparison={hasShare ? <PromisedShareBar share={promisedShare} /> : caption}
                  key={metric.key}
                  label={t(metric.labelKey)}
                  tooltip={metric.tooltipKey ? t(metric.tooltipKey) : undefined}
                  value={
                    hasShare ? <PromisedShareValue amount={amount} share={promisedShare} /> : amount
                  }
                />
              );
            })}
      </div>
    </section>
  );
};
